import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * The time screens (money plan phase 1, 161; api/src/routes/time.ts). Minutes are exact integers, never rounded;
 * describeMinutes() (duration.ts) is the one way they are shown. Every view counts all four kinds of time: task
 * completions (actions included), welfare visits, site visits and communications. Addresses carry only dates, a page
 * and a fixed department word.
 */

export type TimeSource = 'task' | 'visit' | 'site_visit' | 'communication';

/** How each kind of time reads on the page. */
export const SOURCE_LABEL: Record<TimeSource, string> = {
  task: 'Task', visit: 'Welfare visit', site_visit: 'Site visit', communication: 'Communication',
};
export const sourceLabel = (source: string, isAction = false) =>
  isAction ? 'Action' : SOURCE_LABEL[source as TimeSource] ?? source;

export interface ClientTimeEntry {
  on_date: string; source: TimeSource; is_action: boolean; staff_user_id: string; person: string | null;
  department: string; minutes: number; task_id: string | null;
}
export interface ClientTimePage {
  from: string; to: string; entries: ClientTimeEntry[]; more: boolean;
  totals: { minutes: number; by_month: { month: string; minutes: number }[]; by_department: { department: string; minutes: number }[] };
}
export const CLIENT_TIME_PAGE = 100;

/** A client's Time section, a page at a time; the totals cover the whole range on every page. Tiers 1-2 only. */
export function useClientTime(clientId: string, range: { from: string; to: string } | null) {
  const { getToken } = useSession();
  const span = range ? `&from=${range.from}&to=${range.to}` : '';
  return useInfiniteQuery({
    ...queryDefaults,
    queryKey: ['client', clientId, 'time', range?.from ?? 'default', range?.to ?? 'default'],
    queryFn: ({ pageParam }) =>
      apiFetch<ClientTimePage>(`/clients/${clientId}/time?limit=${CLIENT_TIME_PAGE}&offset=${pageParam}${span}`, getToken),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.more ? pages.length * CLIENT_TIME_PAGE : undefined),
  });
}

export interface MyTimeEntry {
  on_date: string; source: TimeSource; is_action: boolean; department: string; minutes: number;
  /** null: no client; 'hidden': a client the reader no longer sees (161, Q8). */
  client: { id: string; ref: string | null; name: string | null } | 'hidden' | null;
}
export interface MyTime {
  week: string; days: { on_date: string; minutes: number }[];
  by_department: { department: string; minutes: number }[]; by_source: { source: TimeSource; minutes: number }[];
  entries: MyTimeEntry[]; total: number;
}

/** My time: ALL of the reader's own time for a week (named by its Monday; '' is this week). */
export function useMyTime(week: string) {
  const { getToken } = useSession();
  return useQuery({
    ...queryDefaults,
    queryKey: ['time', 'mine', week || 'this'],
    queryFn: () => apiFetch<MyTime>(`/time/mine${week ? `?week=${week}` : ''}`, getToken),
  });
}

/** The departments whose time the reader may read: the Department time page offers only these. */
export function useTimeDepartments() {
  const { getToken } = useSession();
  return useQuery({
    ...queryDefaults,
    queryKey: ['time', 'departments'],
    queryFn: () => apiFetch<{ departments: string[] }>('/time/departments', getToken),
    staleTime: 5 * 60_000,
  });
}

export interface DepartmentWeek {
  week: string; department: string; days: string[];
  people: { staff_user_id: string; person: string | null; days: number[]; total: number }[]; total: number;
}
export function useDepartmentWeek(department: string, week: string) {
  const { getToken } = useSession();
  return useQuery({
    ...queryDefaults,
    queryKey: ['time', 'department', department, week || 'this'],
    queryFn: () => apiFetch<DepartmentWeek>(
      `/time/department?department=${encodeURIComponent(department)}${week ? `&week=${week}` : ''}`, getToken),
    enabled: department !== '',
  });
}

export interface DepartmentClients {
  from: string; to: string; department: string; total: number;
  clients: { client_id: string | null; client_ref: string | null; client_name: string | null; minutes: number;
    people: { staff_user_id: string; person: string | null; minutes: number }[]; by_week: { week: string; minutes: number }[] }[];
}
export function useDepartmentClients(department: string, range: { from: string; to: string } | null) {
  const { getToken } = useSession();
  const span = range ? `&from=${range.from}&to=${range.to}` : '';
  return useQuery({
    ...queryDefaults,
    queryKey: ['time', 'department', department, 'clients', range?.from ?? 'this', range?.to ?? 'this'],
    queryFn: () => apiFetch<DepartmentClients>(
      `/time/department/clients?department=${encodeURIComponent(department)}${span}`, getToken),
    enabled: department !== '',
  });
}
